import React, { useCallback, useMemo, useRef, useState } from 'react'

import type { InfiniteData } from '@tanstack/react-query'
import { HiMiniExclamationTriangle, HiMiniXCircle } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import Button from '@/components/common/Button'
import {
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
} from '@/components/common/DropdownMenu'
import { SearchInput } from '@/components/common/TextInputV2'
import { ColorIndicator } from '@/components/tags/ColorIndicator'

import { compareTagsByPosition } from '@/helpers/tags'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useTags } from '@/hooks/tags/useTags'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { Tag, TagType } from '@/types/Tag'

interface TagDropdownMenuSubContentProps {
  onSelect: (tagId: UUID) => void
  selectedTagIds?: UUID[]
  tagClassName?: string
  /** Type of tags to show. Defaults to 'organization' for backwards compatibility. */
  tagType?: TagType
  /** Whether to show the untagged filter option */
  showUntagged?: boolean
  /** Whether the untagged filter is currently enabled */
  untaggedOnly?: boolean
  /** Callback to toggle the untagged filter */
  onToggleUntagged?: () => void
}

interface TagDropdownMenuEditSubContentProps {
  clearActive?: boolean
  isSaving?: boolean
  onCancel: () => void
  onClear: () => void
  onSave: () => void
  onSelect: (tagId: UUID) => void
  saveDisabled?: boolean
  selectedTagIds: UUID[]
  tagType?: TagType
}

interface TagFilterMenuContentProps extends Omit<
  TagDropdownMenuSubContentProps,
  'onToggleUntagged' | 'showUntagged' | 'tagType' | 'untaggedOnly'
> {
  emptyLabel?: string
  tags: Tag[]
  untaggedFilter?: {
    checked: boolean
    onToggle: () => void
  }
}

const selectSortedTags = (data: InfiniteData<PaginatedResponse<Tag>>): Tag[] =>
  data.pages.flatMap((page) => page.data).toSorted(compareTagsByPosition)

export const TagFilterMenuContent: React.FC<TagFilterMenuContentProps> = ({
  emptyLabel = 'No tags available.',
  selectedTagIds,
  onSelect,
  tagClassName,
  tags,
  untaggedFilter,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('')
  const inputRef = useRef<HTMLInputElement>(null)

  const visibleTags = useMemo(() => {
    const sortedTags = tags.toSorted(compareTagsByPosition)
    const normalizedQuery = searchQuery.trim().toLowerCase()

    if (!normalizedQuery) {
      return sortedTags
    }

    return sortedTags.filter((tag) =>
      tag.name.toLowerCase().includes(normalizedQuery),
    )
  }, [searchQuery, tags])

  const handleSelect = useCallback(
    (tagId: UUID) => (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()
      onSelect(tagId)
    },
    [onSelect],
  )

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation()
      setSearchQuery(e.target.value)
    },
    [],
  )

  const handleSearchClick = useCallback(
    (e: React.MouseEvent<HTMLInputElement>) => {
      e.stopPropagation()
    },
    [],
  )

  const handleSearchFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      e.stopPropagation()
    },
    [],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Prevent Radix typeahead from receiving key events when typing in the input
      e.stopPropagation()
    },
    [],
  )

  const tagEmptyLabel = searchQuery.trim() ? 'No tags found.' : emptyLabel

  return (
    <>
      <div className="px-1 pb-2 pt-1">
        <SearchInput
          ref={inputRef}
          type="text"
          placeholder="Search tags..."
          value={searchQuery}
          onChange={handleSearchChange}
          onClick={handleSearchClick}
          onFocus={handleSearchFocus}
          onKeyDown={handleKeyDown}
          autoComplete="off"
        />
      </div>
      {visibleTags.length === 0 ? (
        <div className="px-2 py-2 text-sm text-primary-500 dark:text-primary-400">
          {tagEmptyLabel}
        </div>
      ) : (
        visibleTags.map((tag) => {
          const isSelected = selectedTagIds?.includes(tag.id) ?? false
          return (
            <DropdownMenuCheckboxItem
              key={tag.id}
              onClick={handleSelect(tag.id)}
              checked={isSelected}
              onSelect={(e) => e.preventDefault()}
              className="max-w-[260px] rounded-md"
            >
              <div
                className={twMerge(
                  'flex min-w-0 max-w-full items-center gap-x-2 whitespace-nowrap text-[13px] text-primary-900 dark:text-primary-100',
                  tagClassName,
                )}
              >
                <ColorIndicator color={tag.color} className="shrink-0" />
                <span className="min-w-0 truncate">{tag.name}</span>
              </div>
            </DropdownMenuCheckboxItem>
          )
        })
      )}
      {untaggedFilter && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuCheckboxItem
            checked={untaggedFilter.checked}
            onCheckedChange={untaggedFilter.onToggle}
            onSelect={(e) => e.preventDefault()}
            className="-ml-[0.5px] flex items-center gap-1.5 rounded-md"
          >
            <HiMiniXCircle
              size={14}
              className="flex-shrink-0 text-primary-500 dark:text-primary-400"
            />
            <span className="text-sm">Untagged</span>
          </DropdownMenuCheckboxItem>
        </>
      )}
    </>
  )
}

export const TagDropdownMenuSubContent: React.FC<
  TagDropdownMenuSubContentProps
> = ({
  selectedTagIds,
  onSelect,
  tagClassName,
  tagType = 'organization',
  showUntagged = false,
  untaggedOnly = false,
  onToggleUntagged,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { data: tags } = useTags({
    orgId: activeOrgId,
    tagType,
    selector: selectSortedTags,
  })

  if (!tags) {
    return null
  }

  return (
    <TagFilterMenuContent
      selectedTagIds={selectedTagIds}
      tagClassName={tagClassName}
      tags={tags}
      onSelect={onSelect}
      untaggedFilter={
        showUntagged && onToggleUntagged
          ? { checked: untaggedOnly, onToggle: onToggleUntagged }
          : undefined
      }
    />
  )
}

export const TagDropdownMenuEditSubContent: React.FC<
  TagDropdownMenuEditSubContentProps
> = ({
  clearActive = false,
  isSaving = false,
  onCancel,
  onClear,
  onSave,
  onSelect,
  saveDisabled = false,
  selectedTagIds,
  tagType = 'organization',
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { data: tags } = useTags({
    orgId: activeOrgId,
    tagType,
    selector: selectSortedTags,
  })

  if (!tags) {
    return null
  }

  return (
    <div className="w-[320px] max-w-[calc(100vw-1.5rem)]">
      <div className="max-h-[360px] overflow-y-auto p-1">
        <TagFilterMenuContent
          selectedTagIds={selectedTagIds}
          tags={tags}
          onSelect={onSelect}
        />
      </div>
      <div className="border-t border-primary-200/80 bg-primary-50/80 backdrop-blur-sm dark:border-primary-800 dark:bg-default-dark">
        {clearActive ? (
          <div className="flex items-start gap-2 border-b border-amber-200/80 bg-amber-50/90 px-2.5 py-2 text-[12px] font-medium text-amber-900 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-100">
            <HiMiniExclamationTriangle
              size={14}
              className="mt-0.5 shrink-0 text-amber-500 dark:text-amber-300"
            />
            <span>All tags will be removed when you save.</span>
          </div>
        ) : null}
        <div className="flex items-center justify-between gap-3 p-2">
          <Button
            type="button"
            variant="ghost-compact"
            onClick={onClear}
            disabled={isSaving || (!clearActive && selectedTagIds.length === 0)}
            className="rounded-md px-1.5 text-[13px] text-primary-500 hover:bg-white/70 disabled:hover:bg-transparent dark:text-primary-400 dark:hover:bg-primary-900/80"
          >
            Clear All
          </Button>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="soft-compact"
              onClick={onCancel}
              disabled={isSaving}
              className="rounded-md bg-white/90 px-2.5 text-[13px] text-primary-700 dark:bg-primary-900/80 dark:text-primary-200"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="blue-compact"
              onClick={onSave}
              disabled={saveDisabled || isSaving}
              isLoading={isSaving}
              className="rounded-md px-2.5 text-[13px]"
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
